import type { WistiaPlayerElement } from '@wistia/wistia-player-react';
import { WistiaPlayer } from '@wistia/wistia-player-react';
import { fastBaseHostname } from '~/constantDefinitions';
import { Text } from '@wistia/vhs';
import type { Dispatch, SetStateAction, JSX } from 'react';
import { useCallback, useRef } from 'react';
import styled from 'styled-components';
import type { ApolloQueryResult } from '@apollo/client';
import type { Ev1Player } from 'types/e-v1-player-api-types';
import {
  DEFAULT_RIGHT_PX_WHEN_STICKY_AT_BOTTOM_RIGHT,
  NARROW_WINDOW_WIDTH_PX,
  usePlayerPositionManager,
} from '~/hooks/player/usePlayerPositionManager';
import type { MediaPageCustomizations } from '~/singlePageApp/screens/MediaPreview/customize/MediaPageCustomizationsContext';
import { MemoizedDangerouslyRenderedHtml } from '~/components/MemoizedDangerouslyRendered';
import { isNil } from '~/utilities/type-guards';
import { trackLanguageChangesWithSnowplow } from '~/singlePageApp/utilities/trackLanguageChangesWithSnowplow';
import type { AnonymousMediaScreenPreviewingProps } from '../AnonymousMediaScreen';
import {
  LetterBox,
  LetterBoxLightener,
  StuffBelowMediaContainer,
  TitleAndDescriptionContainer,
} from './Shared';
import { TabbedArea } from '../TabbedArea';
import type { AnonymousMediaQuery } from '../__gql/AnonymousMedia.gql.gen';
import type { MediaPreviewQuery } from '../../MediaPreview/__gql/MediaPreview.gql.gen';
import { SpeakerBios } from '../components/SpeakerBios';

const DEFAULT_FONT_SIZE_PX = 24;
const DEFAULT_FONT_SCALE = 1;

const VideoPlayerWrapper = styled.div<{
  aspectRatio: number;
}>`
  aspect-ratio: ${({ aspectRatio }) => aspectRatio};
  margin: 0 auto;
  max-height: 80vh;
`;

const colorForLetterBoxComponent = ({
  hasLetterboxColor,
  letterboxColor,
  playerColor,
}: {
  hasLetterboxColor: boolean;
  letterboxColor?: string;
  playerColor: string;
}) => {
  return hasLetterboxColor ? letterboxColor : playerColor;
};

const TabbedAreaWrapper = styled.div`
  @media (min-width: ${NARROW_WINDOW_WIDTH_PX}px) {
    max-width: 55vw;
  }
`;

type VideoLayoutProps = Pick<
  AnonymousMediaScreenPreviewingProps,
  'fakeViewportMarginLeftStyle' | 'fakeViewportMarginRightStyle'
> & {
  commentingDisabled: boolean;
  customizations?: MediaPageCustomizations;
  hashedId: string;
  media:
    | NonNullable<AnonymousMediaQuery['anonymousMedia']>
    | NonNullable<MediaPreviewQuery['media']>;
  doNotTrack: boolean;
  testId: string;
  shouldShowTranscriptTab: boolean;
  shouldShowSpeakers: boolean;
  videoEmbed: WistiaPlayerElement | null;
  setVideoEmbed: Dispatch<SetStateAction<Ev1Player | WistiaPlayerElement | null>>;
  fetchMore: (arg: GenericRecord) => Promise<ApolloQueryResult<AnonymousMediaQuery>>;
};

export const VideoLayout = ({
  commentingDisabled,
  customizations,
  hashedId,
  media,
  doNotTrack,
  testId,
  fakeViewportMarginLeftStyle = '0px',
  fakeViewportMarginRightStyle = '0px',
  videoEmbed,
  setVideoEmbed,
  fetchMore,
  shouldShowTranscriptTab,
  shouldShowSpeakers,
}: VideoLayoutProps): JSX.Element => {
  const {
    topLevelAudienceComments,
    topLevelTeamComments,
    id: mediaId,
    name,
    displayDescription,
    playerColor,
    aspectRatio,
    publicCommentsSelection: publicCommentsSelectionFromMedia,
  } = media;
  const videoPlayerWrapperRef = useRef<HTMLDivElement | null>(null);
  const videoPlayerRef = useRef<WistiaPlayerElement | null>(null);
  const tabbedAreaWrapperRef = useRef<HTMLDivElement | null>(null);

  const PlayerPositionManager = usePlayerPositionManager({
    playerParentContainer: videoPlayerWrapperRef.current,
    player: videoEmbed,
    widthStyleWhenStickyAtBottomRight: `min(540px, calc(32vw - ${fakeViewportMarginRightStyle} / 1.25))`,
    rightStyleWhenStickyAtBottomRight: `calc(${fakeViewportMarginRightStyle} + ${DEFAULT_RIGHT_PX_WHEN_STICKY_AT_BOTTOM_RIGHT}px)`,
    rightStyleWhenStickyAtTop: fakeViewportMarginRightStyle,
    leftStyleWhenStickyAtTop: fakeViewportMarginLeftStyle,
  });

  const hasLetterboxColor = Boolean(customizations?.letterboxColor);

  const {
    shouldShowTitle = true,
    shouldShowDescription = true,
    fontFamily = 'inherit',
    fontSize = DEFAULT_FONT_SCALE,
    publicCommentsSelection: publicCommentsSelectionFromCustomizations,
  } = customizations ?? {};

  const publicCommentsSelection = isNil(publicCommentsSelectionFromCustomizations)
    ? publicCommentsSelectionFromMedia
    : publicCommentsSelectionFromCustomizations;

  const onLoadedMetadata = useCallback((): void => {
    const player = videoPlayerRef.current;
    if (player) {
      setVideoEmbed(player);
      trackLanguageChangesWithSnowplow(player, 'anonymous_media_page');
    }
  }, [setVideoEmbed]);

  return (
    <>
      <PlayerPositionManager />
      <LetterBox
        color={colorForLetterBoxComponent({
          hasLetterboxColor,
          letterboxColor: customizations?.letterboxColor,
          playerColor,
        })}
      >
        {!hasLetterboxColor && <LetterBoxLightener />}
        <VideoPlayerWrapper
          ref={videoPlayerWrapperRef}
          // aspect_ratio is only nullable if the media is not a video, image,
          // or (oddly enough) audio. We know we're in a video here so this
          // hackily tells TypeScript we're fine.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          aspectRatio={aspectRatio!}
          data-testid={testId}
        >
          <WistiaPlayer
            doNotTrack={doNotTrack}
            embedHost={fastBaseHostname}
            mediaId={hashedId}
            onLoadedMetadata={onLoadedMetadata}
            ref={videoPlayerRef}
          />
        </VideoPlayerWrapper>
      </LetterBox>
      <StuffBelowMediaContainer style={{ fontFamily }}>
        <TitleAndDescriptionContainer>
          {shouldShowTitle ? (
            <Text
              renderAs="h1"
              style={{ fontSize: `${fontSize * DEFAULT_FONT_SIZE_PX}px` }}
              variant="headline1"
            >
              {name}
            </Text>
          ) : null}
          {displayDescription != null && shouldShowDescription ? (
            <MemoizedDangerouslyRenderedHtml html={displayDescription} />
          ) : null}
        </TitleAndDescriptionContainer>

        {shouldShowSpeakers && <SpeakerBios mediaHashedId={hashedId} />}

        <TabbedAreaWrapper ref={tabbedAreaWrapperRef}>
          <TabbedArea
            audienceComments={topLevelAudienceComments}
            commentingDisabled={commentingDisabled}
            teamComments={topLevelTeamComments}
            mediaId={mediaId}
            mediaHashedId={hashedId}
            shouldShowTranscript={shouldShowTranscriptTab}
            videoEmbed={videoEmbed}
            publicCommentsSelection={publicCommentsSelection}
            fetchMore={fetchMore}
            fontFamily={fontFamily}
          />
        </TabbedAreaWrapper>
      </StuffBelowMediaContainer>
    </>
  );
};
